import type { AccessWindow } from "./types"

/**
 * One pill per card, over the artwork, and **only where access is not simply
 * active**: it has expired, it has not started yet, or it has an end date and so
 * will expire — a digital event, a rental, a subscription with a term.
 *
 * Everything else gets nothing. MLC devotes two table columns to View On / View
 * Until, which works in a table and does not survive the move to a card grid:
 * seven sections x two labelled dates is a wall of numbers, and on most rows
 * neither date constrains anything (a null `viewUntil` is unbounded, so 65 of
 * the 68 seats on the UAT account never expire). A pill on every card is a pill
 * that reads as decoration; the full window rides along in the tooltip of the
 * ones that do appear.
 *
 * An order date is not a window. Memberships carry `orderedOn` and no term, so
 * they are active, unbounded, and unpilled — the date goes in the meta line
 * under the title, which is where MLC's Memberships table puts it too (an
 * `Ordered On` column, not a status).
 */

export type AccessBadgeTone = "expired" | "upcoming" | "ending"

export interface AccessBadgeSpec {
  readonly label: string
  readonly tone: AccessBadgeTone
  /** The full window, for the `title` attribute — nothing is lost by shortening the label. */
  readonly detail: string
}

/** `9/3/2026` → `9/3/26`. The pill is 60px of a card, not a column. */
const short = (date: string): string => date.replace(/\/(\d{2})(\d{2})$/, "/$2")

const detailFor = (accessWindow: AccessWindow): string =>
  [
    accessWindow.viewOn ? `View on ${accessWindow.viewOn}` : undefined,
    accessWindow.viewUntil ? `View until ${accessWindow.viewUntil}` : undefined,
    accessWindow.orderedOn ? `Ordered on ${accessWindow.orderedOn}` : undefined,
  ]
    .filter(Boolean)
    .join(" · ")

export const accessBadge = (accessWindow: AccessWindow | undefined): AccessBadgeSpec | undefined => {
  if (!accessWindow) {
    return undefined
  }

  const detail = detailFor(accessWindow)

  if (accessWindow.state === "expired") {
    return {
      label: accessWindow.viewUntil ? `Expired ${short(accessWindow.viewUntil)}` : "Expired",
      tone: "expired",
      detail,
    }
  }
  if (accessWindow.state === "upcoming") {
    return {
      label: accessWindow.viewOn ? `Available ${short(accessWindow.viewOn)}` : "Available soon",
      tone: "upcoming",
      detail,
    }
  }
  /** An end date at all means it will expire, which is worth saying before it does. */
  if (accessWindow.viewUntil) {
    return { label: `Until ${short(accessWindow.viewUntil)}`, tone: "ending", detail }
  }

  return undefined
}
